// src/services/apiClient.js
import axios from "axios";

/** Should we call localhost:5000/api? */
const USE_LOCAL_API =
  String(process.env.REACT_APP_USE_LOCAL_API || "false").toLowerCase() === "true";

const REMOTE_BASE =
  (process.env.REACT_APP_API_BASE && process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  (process.env.REACT_APP_API_BASE_URL && process.env.REACT_APP_API_BASE_URL.replace(/\/+$/, "")) ||
  "https://api.connectummah.com/api";

const LOCAL_BASE = "http://localhost:5000/api";
const BASE_URL = USE_LOCAL_API ? LOCAL_BASE : REMOTE_BASE;

/** Get token from localStorage or either env name */
const getToken = () => {
  const ls =
    (typeof window !== "undefined" &&
      (localStorage.getItem("authToken") ||
        localStorage.getItem("apiAuthToken") ||
        localStorage.getItem("adminAuthToken"))) ||
    "";
  const envNoUnderscore = process.env.REACT_APP_CONNECTUMMAH_TOKEN || "";
  const envWithUnderscore = process.env.REACT_APP_CONNECT_UMMAH_TOKEN || "";
  return ls || envNoUnderscore || envWithUnderscore || "";
};

const setAuthToken = (token, useAdminApi = false) => {
  if (typeof window === "undefined") return;
  const key = useAdminApi ? "adminAuthToken" : "apiAuthToken";

  if (token) {
    localStorage.setItem(key, token);
    if (!useAdminApi) localStorage.setItem("authToken", token);
  } else {
    localStorage.removeItem(key);
    if (!useAdminApi) localStorage.removeItem("authToken");
  }
};

/** ---------- Public API client ---------- */
const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: false,
});

apiClient.interceptors.request.use((config) => {
  const token = getToken();

  const isFormData =
    config.data &&
    typeof FormData !== "undefined" &&
    config.data instanceof FormData;

  config.headers = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
    Expires: "0",
    ...(config.headers || {}),
  };

  // add cache-buster to GETs
  const method = (config.method || "get").toLowerCase();
  if (method === "get" && config.url) {
    try {
      const isAbsolute = /^https?:\/\//i.test(config.url);
      const base = isAbsolute ? undefined : config.baseURL;
      const urlObj = new URL(config.url, base);
      urlObj.searchParams.set("t", Date.now().toString());
      config.url = isAbsolute
        ? urlObj.toString()
        : urlObj.pathname + urlObj.search;
    } catch {
      // ignore
    }
  }

  return config;
});

apiClient.interceptors.response.use(
  (res) => res,
  (err) => {
    const r = err?.response;
    if (r) {
      if (r.status === 401) {
        console.error(
          "[apiClient] 401 Unauthorized. Missing/invalid token.",
          "Using baseURL:", BASE_URL
        );
      } else if (r.status === 403) {
        console.error("[apiClient] 403 Forbidden. Token lacks scope.");
      } else if (r.status === 404) {
        console.warn(
          "[apiClient] 404 Not Found.",
          "URL:", r.config?.baseURL + r.config?.url
        );
      }
    } else {
      console.error(
        "[apiClient] Network error (no response). Server down/CORS/DNS?",
        err?.message || err
      );
    }
    return Promise.reject(err);
  }
);

/** ---------- Admin API client (named export) ---------- */
const adminApiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: false,
});

adminApiClient.interceptors.request.use((config) => {
  const token = getToken();
  config.headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    "X-Admin-Client": "true",
    ...(config.headers || {}),
  };
  return config;
});

adminApiClient.interceptors.response.use(
  (res) => res,
  (err) => {
    const r = err?.response;
    if (r?.status === 401) {
      console.error("[adminApiClient] 401 Unauthorized.");
    }
    return Promise.reject(err);
  }
);

export default apiClient;
export { apiClient, adminApiClient, getToken, setAuthToken };
